import { useState, useEffect } from 'react';
import { getRegion, fetchLocale } from './helpers';
import { NODE_TYPE } from './constants';

/*
Verify Volume step (Step 1 of Shift Plan) Guardrails
*/

export const NEXT_DAY_PLANNED_VOLUME = {
  type: 'plannedVolume',
  alias: 'PLANNED'
};
export const LAST_DAY_ROLLOVER_VOLUME = {
  type: 'lastDayRolloverVolume',
  alias: 'SIDELINE'
};
export const PLANNED_ROLLOVER_VOLUME = {
  type: 'plannedRolloverVolume',
  alias: 'ROLLOVER'
};

export const VOLUME_GUARDRAILS = {
  NA: {
    PLANNED: {
      MIN_THRESHOLD_PERCENTAGE: 5,
      MAX_THRESHOLD_PERCENTAGE: 5 // additional threshold (+5%)
    },
    SIDELINE: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 5
    },
    ROLLOVER: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 5
    }
  },
  MX: {
    PLANNED: {
      MIN_THRESHOLD_PERCENTAGE: 50,
      MAX_THRESHOLD_PERCENTAGE: 50
    },
    SIDELINE: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 50
    },
    ROLLOVER: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 50
    }
  },
  BR: {
    PLANNED: {
      MIN_THRESHOLD_PERCENTAGE: 50,
      MAX_THRESHOLD_PERCENTAGE: 50
    },
    SIDELINE: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 50
    },
    ROLLOVER: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 50
    }
  },
  AE: {
    PLANNED: {
      MIN_THRESHOLD_PERCENTAGE: 50,
      MAX_THRESHOLD_PERCENTAGE: 50
    },
    SIDELINE: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 50
    },
    ROLLOVER: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 50
    }
  },
  SA: {
    PLANNED: {
      MIN_THRESHOLD_PERCENTAGE: 50,
      MAX_THRESHOLD_PERCENTAGE: 50
    },
    SIDELINE: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 50
    },
    ROLLOVER: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 50
    }
  },
  EG: {
    PLANNED: {
      MIN_THRESHOLD_PERCENTAGE: 50,
      MAX_THRESHOLD_PERCENTAGE: 50
    },
    SIDELINE: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 50
    },
    ROLLOVER: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 50
    }
  },
  EU: {
    PLANNED: {
      MIN_THRESHOLD_PERCENTAGE: 100,
      MAX_THRESHOLD_PERCENTAGE: 25 // additional threshold (+25%)
    },
    SIDELINE: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 25
    },
    ROLLOVER: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 25
    }
  },
  DEFAULT: {
    PLANNED: {
      MIN_THRESHOLD_PERCENTAGE: 100,
      MAX_THRESHOLD_PERCENTAGE: 25
    },
    SIDELINE: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 25
    },
    ROLLOVER: {
      MIN_THRESHOLD_PERCENTAGE: 0,
      MAX_THRESHOLD_PERCENTAGE: 25
    }
  }
};

/*
Verify Attendance step (Step 2 of Shift Plan) Guardrails
*/

export const ATTENDANCE_GUARDRAILS = {
  NA: {
    ATTENDANCE_OVERRIDE_LIMIT: 50,
    ATTENDANCE_LOWER_LIMIT: 0,
    ATTENDANCE_UPPER_LIMIT: 100
  },
  MX: {
    ATTENDANCE_OVERRIDE_LIMIT: 100,
    ATTENDANCE_LOWER_LIMIT: 0,
    ATTENDANCE_UPPER_LIMIT: 100
  },
  BR: {
    ATTENDANCE_OVERRIDE_LIMIT: 100,
    ATTENDANCE_LOWER_LIMIT: 0,
    ATTENDANCE_UPPER_LIMIT: 100
  },
  AE: {
    ATTENDANCE_OVERRIDE_LIMIT: 100,
    ATTENDANCE_LOWER_LIMIT: 0,
    ATTENDANCE_UPPER_LIMIT: 100
  },
  SA: {
    ATTENDANCE_OVERRIDE_LIMIT: 100,
    ATTENDANCE_LOWER_LIMIT: 0,
    ATTENDANCE_UPPER_LIMIT: 100
  },
  EG: {
    ATTENDANCE_OVERRIDE_LIMIT: 100,
    ATTENDANCE_LOWER_LIMIT: 0,
    ATTENDANCE_UPPER_LIMIT: 100
  },
  EU: {
    ATTENDANCE_OVERRIDE_LIMIT: 50,
    ATTENDANCE_LOWER_LIMIT: 0,
    ATTENDANCE_UPPER_LIMIT: 100
  },
  DEFAULT: {
    ATTENDANCE_OVERRIDE_LIMIT: 50,
    ATTENDANCE_LOWER_LIMIT: 0,
    ATTENDANCE_UPPER_LIMIT: 100
  },
  RSR: {
    ATTENDANCE_OVERRIDE_LIMIT: 50,
    ATTENDANCE_LOWER_LIMIT: 0,
    ATTENDANCE_UPPER_LIMIT: 100
  }
};

/*
Verify Rates step (Step 3 of Shift Plan) Guardrails
*/

export const RATES_GUARDRAILS = {
  NA: {
    RATES_MIN_THRESHOLD: 0.95,
    RATES_MAX_THRESHOLD: 1.05,
    RATES_THRESHOLD_PERCENTAGE: 5
  },
  MX: {
    RATES_MIN_THRESHOLD: 0.5,
    RATES_MAX_THRESHOLD: 1.5,
    RATES_THRESHOLD_PERCENTAGE: 50
  },
  BR: {
    RATES_MIN_THRESHOLD: 0.5,
    RATES_MAX_THRESHOLD: 1.5,
    RATES_THRESHOLD_PERCENTAGE: 50
  },
  AE: {
    RATES_MIN_THRESHOLD: 0.5,
    RATES_MAX_THRESHOLD: 1.5,
    RATES_THRESHOLD_PERCENTAGE: 50
  },
  SA: {
    RATES_MIN_THRESHOLD: 0.5,
    RATES_MAX_THRESHOLD: 1.5,
    RATES_THRESHOLD_PERCENTAGE: 50
  },
  EG: {
    RATES_MIN_THRESHOLD: 0.5,
    RATES_MAX_THRESHOLD: 1.5,
    RATES_THRESHOLD_PERCENTAGE: 50
  },
  EU: {
    RATES_MIN_THRESHOLD: 0.75,
    RATES_MAX_THRESHOLD: 1.25,
    RATES_THRESHOLD_PERCENTAGE: 25
  },
  DEFAULT: {
    RATES_MIN_THRESHOLD: 0.75,
    RATES_MAX_THRESHOLD: 1.25,
    RATES_THRESHOLD_PERCENTAGE: 25
  },
  RSR: {
    RATES_MIN_THRESHOLD: 0.6,
    RATES_MAX_THRESHOLD: 1.4,
    RATES_THRESHOLD_PERCENTAGE: 40
  }
};

export const getGuardrails = async (guardrails, nodeType) => {
  // Check for node-specific guardrails first (e.g., RSR)
  if (nodeType === NODE_TYPE.RSR && nodeType in guardrails) {
    return guardrails[nodeType];
  }

  // Check for locale-specific guardrails
  const countryCode = await fetchLocale();
  if (countryCode && countryCode in guardrails) {
    return guardrails[countryCode];
  }

  // Check for region-specific guardrails
  const region = getRegion().toUpperCase();
  if (region && region in guardrails) {
    return guardrails[region];
  }

  // Fall back to default
  return guardrails['DEFAULT'];
};

export const getMaxAllowedVolumePercentage = async (volumeType, nodeType) => {
  const volumeAlias =
    volumeType === NEXT_DAY_PLANNED_VOLUME.type
      ? NEXT_DAY_PLANNED_VOLUME.alias
      : LAST_DAY_ROLLOVER_VOLUME.type
      ? LAST_DAY_ROLLOVER_VOLUME.alias
      : PLANNED_ROLLOVER_VOLUME.alias;
  const guardrails = await getGuardrails(VOLUME_GUARDRAILS, nodeType);
  return guardrails[volumeAlias].MAX_THRESHOLD_PERCENTAGE;
};

export const useGuardrails = (guardrailsType, nodeType) => {
  const [guardrails, setGuardrails] = useState(guardrailsType['DEFAULT']);

  useEffect(() => {
    const fetchGuardrails = async () => {
      try {
        const result = await getGuardrails(guardrailsType, nodeType);
        setGuardrails(result);
      } catch (err) {
        console.error('Failed to fetch guardrails, using default values:', err);
        setGuardrails(guardrailsType['DEFAULT']);
      }
    };

    fetchGuardrails();
  }, [guardrailsType, nodeType]);

  return { guardrails };
};
